/**
 * Applications Controller Module
 * Handles Applications page initialization and data loading
 *
 * Following OOA/OOD principles:
 * - Single Responsibility: Only applications page logic
 * - Template Method: Uses BaseController hooks
 */

import { PageName } from '../types/page-types';
import { BaseController, ControllerServices } from './base-controller';

/**
 * Application data structure
 */
interface Application {
    application_id: string;
    name?: string;
    application_type?: string;
    status?: string;
    port?: string;
    version?: string;
    [key: string]: any;
}

/**
 * ApplicationsController class
 * Manages the applications page
 */
export class ApplicationsController extends BaseController {
    private allApplications: Application[] = [];

    constructor(services: ControllerServices) {
        super(services);
    }

    getPageName(): PageName {
        return 'applications';
    }

    protected async doInit(): Promise<void> {
        await this.loadApplicationsData();
        this.setupFilterListeners();
    }

    /**
     * Setup filter event listeners
     */
    private setupFilterListeners(): void {
        const applyBtn = document.getElementById('apply-app-filters');
        const resetBtn = document.getElementById('reset-app-filters');
        const searchInput = document.getElementById('app-search') as HTMLInputElement;

        if (applyBtn) {
            applyBtn.addEventListener('click', () => this.applyFilters());
        }

        if (resetBtn) {
            resetBtn.addEventListener('click', () => this.resetFilters());
        }

        // Also filter on Enter key in search box
        if (searchInput) {
            searchInput.addEventListener('keypress', (e) => {
                if (e.key === 'Enter') {
                    this.applyFilters();
                }
            });
        }

        console.log('Filter listeners setup complete');
    }

    /**
     * Apply filters to applications list
     */
    private applyFilters(): void {
        const searchInput = document.getElementById('app-search') as HTMLInputElement;
        const typeFilter = document.getElementById('app-type-filter') as HTMLSelectElement;
        const statusFilter = document.getElementById('app-status-filter') as HTMLSelectElement;

        const searchTerm = searchInput?.value?.toLowerCase() || '';
        const typeValue = typeFilter?.value || '';
        const statusValue = statusFilter?.value || '';

        console.log(`Applying filters: search="${searchTerm}", type="${typeValue}", status="${statusValue}"`);

        let filtered = this.allApplications;

        // Search filter (searches name, type, hostname, device_id)
        if (searchTerm) {
            filtered = filtered.filter(app => {
                const name = (app.name || app.application_id || '').toLowerCase();
                const type = (app.app_type || app.application_type || '').toLowerCase();
                const hostname = (app.hostname || '').toLowerCase();
                const deviceId = (app.device_id || '').toLowerCase();
                const role = (app.role || '').toLowerCase();

                return name.includes(searchTerm) ||
                       type.includes(searchTerm) ||
                       hostname.includes(searchTerm) ||
                       deviceId.includes(searchTerm) ||
                       role.includes(searchTerm);
            });
        }

        // Type filter
        if (typeValue) {
            filtered = filtered.filter(app => {
                const appType = (app.app_type || app.application_type || '').toLowerCase();
                return appType.includes(typeValue.toLowerCase());
            });
        }

        // Status filter
        if (statusValue) {
            filtered = filtered.filter(app => app.status === statusValue);
        }

        console.log(`Filtered to ${filtered.length} applications`);
        this.populateApplicationsTable(filtered);
        this.updatePaginationInfo(filtered.length);
    }

    /**
     * Reset all filters
     */
    private resetFilters(): void {
        const searchInput = document.getElementById('app-search') as HTMLInputElement;
        const typeFilter = document.getElementById('app-type-filter') as HTMLSelectElement;
        const statusFilter = document.getElementById('app-status-filter') as HTMLSelectElement;

        if (searchInput) searchInput.value = '';
        if (typeFilter) typeFilter.value = '';
        if (statusFilter) statusFilter.value = '';

        console.log('Filters reset');
        this.populateApplicationsTable(this.allApplications);
        this.updatePaginationInfo(this.allApplications.length);
    }

    /**
     * Update pagination info text
     */
    private updatePaginationInfo(count: number): void {
        const infoEl = document.getElementById('applications-pagination-info');
        if (infoEl) {
            infoEl.textContent = `Showing 1-${count} of ${count}`;
        }
    }

    /**
     * Load applications data from vector_db
     */
    public async loadApplicationsData(): Promise<void> {
        console.log('Loading applications data from vector_db...');

        if (!this.canisterClient) {
            console.error('CanisterClient not available');
            return;
        }

        try {
            const result = await this.canisterClient.callCanister('vector_db', 'get_all_applications', []);
            console.log('Applications result:', result);

            if (result && Array.isArray(result)) {
                // Convert tuple format to application objects
                const applications = result.map((tuple: any) => {
                    const [appId, metadata] = tuple;
                    return {
                        application_id: appId,
                        ...metadata
                    };
                });
                console.log(`Parsed ${applications.length} applications`);

                this.allApplications = applications;
                this.updateApplicationsStats(applications);
                this.populateApplicationsTable(applications);
            }
        } catch (error) {
            console.error('Error loading applications:', error);
        }
    }

    /**
     * Update applications statistics
     */
    private updateApplicationsStats(applications: Application[]): void {
        const total = applications.length;
        const running = applications.filter(a => a.status === 'Running').length;
        const warning = applications.filter(a => a.status === 'Warning').length;

        // Update stats cards (match HTML element IDs)
        this.updateElementText('total-apps-count', total);
        this.updateElementText('running-apps-count', running);
        this.updateElementText('warning-apps-count', warning);

        console.log(`Updated application stats: ${total} total, ${running} running, ${warning} warning`);

        // Also load relationships count
        this.loadRelationshipsCount();
    }

    /**
     * Load relationships count from vector_db
     */
    private async loadRelationshipsCount(): Promise<void> {
        try {
            const result = await this.canisterClient?.callCanister('vector_db', 'get_all_relationships', []);
            if (result && Array.isArray(result)) {
                this.updateElementText('relationships-count', result.length);
                console.log(`Updated relationships count: ${result.length}`);
            }
        } catch (error) {
            console.error('Error loading relationships count:', error);
        }
    }

    /**
     * Populate the applications table
     */
    private populateApplicationsTable(applications: Application[]): void {
        const tbody = document.getElementById('applications-table-body');
        if (!tbody) {
            console.error('Applications table body not found');
            return;
        }

        tbody.innerHTML = '';

        if (applications.length === 0) {
            tbody.innerHTML = '<tr><td colspan="6" class="text-center py-4 text-gray-500">No applications found</td></tr>';
            return;
        }

        applications.forEach(app => {
            const row = document.createElement('tr');
            row.className = 'border-t hover:bg-gray-50';

            const statusClass = app.status === 'Running' ? 'bg-green-100 text-green-800' :
                               app.status === 'Stopped' ? 'bg-red-100 text-red-800' :
                               app.status === 'Warning' ? 'bg-yellow-100 text-yellow-800' :
                               'bg-gray-100 text-gray-800';

            // Match HTML table columns: Name, Type, Version, Host Device, Status, Port, Actions
            row.innerHTML = `
                <td class="py-3 px-4">${this.escapeHtml(app.name || app.application_id || 'Unknown')}</td>
                <td class="py-3 px-4">${this.escapeHtml(app.app_type || app.application_type || 'Unknown')}</td>
                <td class="py-3 px-4">${this.escapeHtml(app.version || 'N/A')}</td>
                <td class="py-3 px-4">${this.escapeHtml(app.hostname || app.device_id || 'N/A')}</td>
                <td class="py-3 px-4">
                    <span class="px-2 py-1 text-xs font-semibold rounded-full ${statusClass}">
                        ${this.escapeHtml(app.status || 'Unknown')}
                    </span>
                </td>
                <td class="py-3 px-4">${this.escapeHtml(app.port || 'N/A')}</td>
                <td class="py-3 px-4">
                    <button class="text-blue-600 hover:text-blue-800 text-sm" onclick="viewApplication('${app.application_id}')">
                        View
                    </button>
                </td>
            `;

            tbody.appendChild(row);
        });

        console.log(`Populated applications table with ${applications.length} rows`);
    }

    /**
     * Get all applications (for external access)
     */
    public getAllApplications(): Application[] {
        return this.allApplications;
    }
}
